import { Text } from '@mantine/core';
import { useQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFilteredRowModel,
	createSortedRowModel,
	useTable,
} from '@tanstack/react-table';

import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import type { PlantListing, PlantsFilters } from '@apple/features/plants/models/listing';
import { getPlantsDataTableQueryOptions } from '@apple/features/plants/queries/odata';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	sortedRowModel: createSortedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

export const Route = createFileRoute('/_authed/_admin/manage/plants')({
	beforeLoad: args =>
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.PlantManagement.Read',
		}),
	component: PlantManagementRoute,
});

function PlantManagementRoute() {
	const { t } = useTranslation('manage');
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();

	const tableState = useTableState<PlantListing, PlantsFilters>({
		search,
		navigate,
		defaultSorting: [{ id: 'name', desc: true }],
		fieldMap: [],
	});

	const [plantsQuery] = useQueries({
		queries: [
			getPlantsDataTableQueryOptions({
				filters: tableState.currentFilterData,
				pagination: tableState.state.pagination,
				commonFilters: {},
				sorting: tableState.state.sorting,
			}),
		],
	});

	const table = useTable({
		features,
		data: plantsQuery.data.rows,
		rowCount: plantsQuery.data.totalRowCount,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: true,
		manualFiltering: true,
		manualSorting: true,
		manualPagination: true,
		columnResizeMode: 'onChange',
		columns: [
			{
				id: 'name',
				accessorKey: 'name',
				header: t('plants.fields.plantName'),
				filter: {
					group: t('plants.filterGroups.details'),
				},
				cell: ({ row }) => (
					<Link
						to='/manage/plants/$warehouseLocationId'
						state={{ from: location }}
						params={{ warehouseLocationId: row.original.warehouseLocationId }}
						title={t('plants.links.view')}
					>
						<Text size='sm'>{row.original.name}</Text>
					</Link>
				),
			},
			{
				id: 'code',
				accessorKey: 'code',
				header: t('plants.fields.plantCode'),
				size: 100,
				filter: {
					group: t('plants.filterGroups.details'),
				},
			},
			{
				id: 'abbreviation',
				accessorKey: 'abbreviation',
				header: t('plants.fields.plantAbbreviation'),
				size: 100,
				filter: {
					group: t('plants.filterGroups.details'),
				},
			},
			{
				id: 'salesOrganization',
				accessorKey: 'salesOrganization',
				header: t('plants.fields.salesOrganization'),
				size: 100,
				filter: {
					group: t('plants.filterGroups.details'),
				},
			},
			{
				id: 'currency',
				accessorKey: 'currency',
				header: t('plants.fields.currency'),
				size: 100,
				filter: {
					group: t('plants.filterGroups.details'),
				},
			},
		],
	});

	return (
		<TableLayout table={table} title={t('plants.title')}>
			<DataTable table={table} loading={plantsQuery.isFetching} />
		</TableLayout>
	);
}
